"use client";

/**
 * Price Check strip for the LIVE /compare view (2026-09-04, owner-approved
 * extension of the /c-page feature). The search itself computes and stores
 * the strip payload when it writes the page cache; this component fetches
 * that 1KB payload once the stream completes and renders the SAME
 * PriceCheckStrip component the /c page uses - one widget, one look.
 *
 * Timing: the publisher binds the slug and the cache write lands moments
 * after the complete event, so we poll with backoff (1s, 2s, 4s, 8s) and
 * give up silently - a search that never published a slug simply shows no
 * strip, exactly like a /c page with no history. Zero impact on the search
 * critical path; one tiny GET after results are already on screen.
 */

import { useEffect, useRef, useState } from "react";

import { PriceCheckStrip } from "@/components/comparison/PriceCheckStrip";

const DELAYS_MS = [1000, 2000, 4000, 8000];

export function LivePriceCheck({
  slug,
  active,
}: {
  slug: string | null;
  active: boolean;
}) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!active || !slug || startedRef.current) return;
    startedRef.current = true;
    let cancelled = false;

    (async () => {
      for (const delay of DELAYS_MS) {
        await new Promise((r) => setTimeout(r, delay));
        if (cancelled) return;
        try {
          const res = await fetch(
            `/api/pseo/price-check/${encodeURIComponent(slug)}`,
            { cache: "no-store" },
          );
          if (!res.ok) continue;
          const body = (await res.json()) as {
            price_check?: Record<string, unknown> | null;
          };
          if (body.price_check) {
            if (!cancelled) setData(body.price_check);
            return;
          }
        } catch {
          // transient - next backoff step retries
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, slug]);

  if (!data) return null;
  return <PriceCheckStrip data={data as never} />;
}
